import { RelocationOrderTransportMethod, RelocationOrderTransportReason } from "@/api-schema-enums";
import {
  getDateInputValidationMessage,
  getStartOfToday,
  inputStringToDate,
  validateDateInputString,
} from "@/common/helpers/date-input";
import { sortByLabel } from "@/common/helpers/sort-by-label";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { RELOCATION_ORDER_TRANSPORT_REASON_TRANSLATIONS } from "@/components/relocation-order/relocation-order-transport-reason-translations";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCard } from "@/components/ui/form-card";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { z } from "zod";
import {
  RelocationOrderType,
  useRelocationOrderInitStore,
} from "@/modules/fit/internal/relocation-order/init/_hooks/use-relocation-order-init-store";
import { useRelocationOrderInitSubmit } from "@/modules/fit/internal/relocation-order/init/_hooks/use-relocation-order-init-submit";

const baseFormSchema = z.object({
  earliestPickupDate: z.string().trim(),
  transportReason: z.enum(RelocationOrderTransportReason),
});

type FormValues = z.infer<typeof baseFormSchema>;

export function InitSetDetailsView() {
  const { t } = useTranslation(["common", "fit"]);
  const [_, navigate] = useLocation();

  const hasHydrated = useRelocationOrderInitStore((state) => state.hasHydrated);
  const vehicle = useRelocationOrderInitStore((state) => state.vehicle);
  const relocationOrderType = useRelocationOrderInitStore((state) => state.relocationOrderType);
  const transportMethod = useRelocationOrderInitStore((state) => state.transportMethod);
  const originExternalAddress = useRelocationOrderInitStore((state) => state.originExternalAddress);
  const destinationBranch = useRelocationOrderInitStore((state) => state.destinationBranch);
  const destinationExternalAddress = useRelocationOrderInitStore((state) => state.destinationExternalAddress);
  const transportReason = useRelocationOrderInitStore((state) => state.transportReason);
  const setEarliestPickupDate = useRelocationOrderInitStore((state) => state.setEarliestPickupDate);
  const setTransportReason = useRelocationOrderInitStore((state) => state.setTransportReason);
  const reset = useRelocationOrderInitStore((state) => state.reset);

  const { mutateAsync, isPending } = useRelocationOrderInitSubmit();

  useEffect(() => {
    if (!hasHydrated) {
      return;
    }

    if (!vehicle) {
      navigate("~/fit/relocation-order/init/select-vehicle", { replace: true });
      return;
    }

    switch (relocationOrderType) {
      case RelocationOrderType.BranchToBranch:
        if (!destinationBranch) {
          navigate("~/fit/relocation-order/init/select-destination-branch", { replace: true });
        }
        break;

      case RelocationOrderType.BranchToExternalAddress:
        if (!destinationExternalAddress) {
          navigate("~/fit/relocation-order/init/set-destination-external-address", { replace: true });
        }
        break;

      case RelocationOrderType.ExternalAddressToBranch:
        if (!originExternalAddress) {
          navigate("~/fit/relocation-order/init/set-origin-external-address", { replace: true });
          return;
        }

        if (!destinationBranch) {
          navigate("~/fit/relocation-order/init/select-destination-branch", { replace: true });
        }
        break;
    }
  }, [
    destinationBranch,
    destinationExternalAddress,
    hasHydrated,
    navigate,
    originExternalAddress,
    relocationOrderType,
    vehicle,
  ]);

  const branchSetsPickupDate = transportMethod === RelocationOrderTransportMethod.OwnAxle;
  const formSchema = useMemo(
    () =>
      baseFormSchema.superRefine((values, context) => {
        if (!branchSetsPickupDate) {
          return;
        }

        if (!values.earliestPickupDate) {
          context.addIssue({
            code: "custom",
            path: ["earliestPickupDate"],
            message: "common:PickupFromIsRequired",
          });
          return;
        }

        const error = validateDateInputString(values.earliestPickupDate, { disallowPastDate: true });

        if (error) {
          context.addIssue({
            code: "custom",
            path: ["earliestPickupDate"],
            message: getDateInputValidationMessage(error),
          });
        }
      }),
    [branchSetsPickupDate],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      earliestPickupDate: "",
      transportReason: transportReason ?? undefined,
    },
  });
  const { isSubmitted, touchedFields } = form.formState;
  const showEarliestPickupDateError = !!touchedFields.earliestPickupDate || isSubmitted;

  const transportReasonOptions = useMemo(
    () =>
      sortByLabel(
        Object.values(RelocationOrderTransportReason).map((reason) => ({
          value: reason,
          label: t(RELOCATION_ORDER_TRANSPORT_REASON_TRANSLATIONS[reason]),
        })),
        (option) => option.label,
        { getValue: (option) => option.value },
      ),
    [t],
  );

  const canRenderDetailsStep =
    hasHydrated &&
    !!vehicle &&
    ((relocationOrderType === RelocationOrderType.BranchToBranch && !!destinationBranch) ||
      (relocationOrderType === RelocationOrderType.BranchToExternalAddress && !!destinationExternalAddress) ||
      (relocationOrderType === RelocationOrderType.ExternalAddressToBranch &&
        !!originExternalAddress &&
        !!destinationBranch));

  if (!canRenderDetailsStep) {
    return null;
  }

  const onSubmit = async (values: FormValues) => {
    const earliestPickupDate = branchSetsPickupDate ? inputStringToDate(values.earliestPickupDate)! : null;
    if (earliestPickupDate) {
      setEarliestPickupDate(earliestPickupDate);
    }
    setTransportReason(values.transportReason);

    await toastMutationStatus({
      mutationFn: () => mutateAsync(earliestPickupDate, values.transportReason),
      successMessage: t("fit:RelocationOrderCreated"),
      errorMessage: t("fit:ErrorCreatingOrder"),
      onSuccess: () => {
        reset();
        navigate("~/fit/relocation-order");
      },
    });
  };

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Form}
      title={t("fit:SetDetails")}
      subTitle={t("fit:SetDetailsDescription")}
    >
      <Form {...form}>
        <form className="flex w-full flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)}>
          <FormCard title={t("fit:SetDetails")} titleHidden contentClassName="space-y-6">
            {branchSetsPickupDate && (
              <FormField
                control={form.control}
                name="earliestPickupDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel showError={showEarliestPickupDateError}>{t("common:PickupFrom")}</FormLabel>
                    <FormControl showError={showEarliestPickupDateError}>
                      <DatePickerInput
                        ref={field.ref}
                        name={field.name}
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        onBlur={() => {
                          field.onBlur();
                          void form.trigger("earliestPickupDate");
                        }}
                        disabledDates={{ before: getStartOfToday() }}
                      />
                    </FormControl>
                    <FormMessage showError={showEarliestPickupDateError} />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="transportReason"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t("fit:TransportReason")}</FormLabel>
                  <Combobox
                    options={transportReasonOptions}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    placeholder={t("fit:SelectTransportReason")}
                    searchPlaceholder={t("fit:SearchTransportReason")}
                    emptyText={t("fit:NoTransportReasonFound")}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormCard>

          <div className="flex flex-row justify-end">
            <Button type="submit" disabled={!form.formState.isValid || isPending}>
              {isPending && <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />}
              {t("common:Create")}
            </Button>
          </div>
        </form>
      </Form>
    </CenteredLayout>
  );
}
